import { createComposeRoute } from 'app/utils/routes';
import routeConfig from 'app/config/routes';

export const composeRoute = createComposeRoute(routeConfig);
export const validateModalRoute = (route: string) => {
  return Object.values(modalRoutes).includes(route);
};

const routes = {
  LOGIN: composeRoute('public.routes.login.path'),
  REGISTRATION: composeRoute('public.routes.registration.path'),
  FORGOT_PASSWORD: composeRoute('public.routes.forgotPassword.path'),
  FORGOT_PASSWORD_SUBMIT_EMAIL: composeRoute(
    'public.routes.forgotPassword.routes.submitEmail.path',
  ),
  FORGOT_PASSWORD_SUBMIT_CODE: composeRoute(
    'public.routes.forgotPassword.routes.submitCode.path',
  ),
  FORGOT_PASSWORD_SET_PASSWORD: composeRoute(
    'public.routes.forgotPassword.routes.setPassword.path',
  ),
  PUBLIC_ANALYTICS_REPORTS_VIEW: composeRoute(
    'public.routes.analyticsReports.routes.view.path',
  ),
  PUBLIC_ANALYTICS_EXPORTS_VIEW: composeRoute(
    'public.routes.analyticsExports.routes.view.path',
  ),
  PUBLIC_PDF_REPORTS: composeRoute('public.routes.pdfReports.path'),
  PUBLIC_PDF_REPORTS_INSIGHTS_POSTS_PERFORMANCE: composeRoute(
    'public.routes.pdfReports.routes.insightsPostsPerformance.path',
  ),
  PUBLIC_PDF_REPORTS_INSIGHTS_COMPETITION_ANALYSIS: composeRoute(
    'public.routes.pdfReports.routes.insightsCompetitionAnalysis.path',
  ),
  PUBLIC_PDF_REPORTS_INSIGHTS_PAGE_PERFORMANCE_CURRENT_SELECTION: composeRoute(
    'public.routes.pdfReports.routes.insightsPagePerformance.routes.currentSelection.path',
  ),
  PUBLIC_PDF_REPORTS_INSIGHTS_PAGE_PERFORMANCE_OVERVIEW: composeRoute(
    'public.routes.pdfReports.routes.insightsPagePerformance.routes.overview.path',
  ),
  PUBLIC_PDF_REPORTS_INSIGHTS_LITE: composeRoute(
    'public.routes.pdfReports.routes.insightsLite.path',
  ),
  RESET_PASSWORD: composeRoute('public.routes.resetPassword.path'),
  DASHBOARD: composeRoute('dashboard.path'),
  POSTS: composeRoute('posts.path'),
  POSTS_CALENDAR: composeRoute('posts.routes.calendar.path'),
  POSTS_KANBAN: composeRoute('posts.routes.kanban.path'),
  POSTS_GRID: composeRoute('posts.routes.grid.path'),
  POSTS_LIST: composeRoute('posts.routes.list.path'),
  REPORTS: composeRoute('reports.path'),
  NEW_REPORT: composeRoute('reports.routes.new.path'),
  MY_REPORTS: composeRoute('reports.routes.my.path'),
  INSIGHTS: composeRoute('insights.path'),
  INSIGHTS_POSTS_PERFORMANCE: composeRoute(
    'insights.routes.postsPerformance.path',
  ),
  INSIGHTS_PAGE_PERFORMANCE: composeRoute(
    'insights.routes.pagePerformance.path',
  ),
  INSIGHTS_COMPETITION_ANALYSIS: composeRoute(
    'insights.routes.competitionAnalysis.path',
  ),
  INSIGHTS_PERFORMANCE_HUB: composeRoute('insights.routes.performanceHub.path'),
  ANALYTICS: composeRoute('analytics.path'),
  ANALYTICS_OVERVIEW: composeRoute('analytics.routes.overview.path'),
  ANALYTICS_PERFORMANCE: composeRoute('analytics.routes.performance.path'),
  ANALYTICS_CONTENT: composeRoute('analytics.routes.content.path'),
  ANALYTICS_COMPETITION_ANALYSIS: composeRoute(
    'analytics.routes.competitionAnalysis.path',
  ),
  ANALYTICS_REPORTS: composeRoute('analytics.routes.reports.path'),
  ANALYTICS_REPORTS_CREATE: composeRoute(
    'analytics.routes.reports.routes.create.path',
  ),
  INSIGHTS_LITE: composeRoute('insightsLite'),
  IN_APP_NOTIFICATIONS: composeRoute('notifications.routes.history.path'),
  AI_CONTENT: composeRoute('aiContent.path'),
  BRAND_HUB: composeRoute('brandHub.path'),
  ONBOARDING_SETUP_ACCOUNT: composeRoute(
    'onboarding.routes.initSetupAccount.path',
  ),
  ONBOARDING_CONNECT_PROFILES: composeRoute(
    'onboarding.routes.connectProfiles.path',
  ),
  POSTS_APPROVALS: composeRoute('approvals.routes.posts.path'),
  BIO: composeRoute('bio.path'),
  BIO_CREATE: composeRoute('bio.routes.create.path'),
  BIO_EDITOR: composeRoute('bio.routes.editor.path'),
  BIO_ANALYTICS: composeRoute('bio.routes.analytics.path'),
  SETTINGS: {
    AUTHORIZATION: composeRoute('settings.routes.authorization.path'),
    BILLING: composeRoute('settings.routes.billing.path'),
    BILLING_RETURN: composeRoute('settings.routes.billing.routes.return.path'),
    USERS: composeRoute('settings.routes.users.path'),
    SECURITY: composeRoute('settings.routes.security.path'),
    PERSONAL: composeRoute('settings.routes.personal.path'),
    PROFILES: composeRoute('settings.routes.profiles.path'),
    GLOBAL_PROFILES: composeRoute('settings.routes.globalProfiles.path'),
    PROFILE_GROUPS: composeRoute('settings.routes.profileGroups.path'),
    COMPANY: composeRoute('settings.routes.company.path'),
  },
};

export function getCompanySettingsSectionRoute(section: string): string {
  return `${routes.SETTINGS.COMPANY}/${section}`;
}

export function getProfileDetailRoute({
  profileId,
  tab,
  isGlobalContentManager = false,
}: {
  profileId: number;
  tab?: string;
  isGlobalContentManager?: boolean;
}): string {
  const listRoute = isGlobalContentManager
    ? routes.SETTINGS.GLOBAL_PROFILES
    : routes.SETTINGS.PROFILES;
  const detailRoute = `${listRoute}/${profileId}`;

  return tab ? `${detailRoute}/${tab}` : detailRoute;
}

export function getAnalyticsReportEditRoute(reportId: number): string {
  return `${routes.ANALYTICS_REPORTS}/${reportId}/edit`;
}

export const modalRoutes = {
  POST_DETAIL: 'post',
  POST_DETAIL_NEW: 'post-workspace',
  POST_REVISION_DETAIL: 'post-revision',
  INSPIRATION_DETAIL: 'inspiration',
  PUBLIC_INVITE_USER: 'public-invite-user',
  NEW_PROFILE: 'new-profile',
  ASSOCIATE_POSTS_PROFILE: 'associate-posts-profile',
  NEW_CONTENT_PLAN: 'new-content-plan',
  REFERRAL_PROGRAM: 'referral-program',
  MANAGE_PROFILE_GROUP: 'manage-profile-group',
  SHARE_PREVIEW: 'share-preview',
  WELCOME: 'welcome',
  SELECT_SINGLE_PROFILE_BEFORE_COMPOSE: 'select-single-profile-before-compose',
  FIRST_POST_APPROVED: 'first-post-approved',
  // Posts - Time savers
  ASSIGN_MULTIPLE_TASKS: 'assign-multiple-tasks',
  BULK_APPROVAL: 'bulk-approval',
  CHANGE_POSTS_STATUS: 'change-posts-status',
  RESPOND_TO_APPROVAL_REQUEST: 'respond-to-approval-request',
  DUPLICATE_POSTS: 'duplicate-posts',
  MOVE_POSTS: 'move-posts',
  MOVE_POSTS_TO_TRASH: 'move-posts-to-trash',
  SCHEDULE_POSTS: 'schedule-posts',
  SEND_FOR_CLIENT_APPROVAL: 'send-for-client-approval',
  SEND_FOR_INTERNAL_APPROVAL: 'send-for-internal-approval',
  TRIAL: {
    START: 'trial-start',
    EXPIRED: 'trial-expired',
  },
  SUBSCRIPTION: 'subscription-expired',
  PROFILE_LIMIT_REACHED: 'profile-limit-reached',
  USER_LIMIT_REACHED: 'user-limit-reached',
  BRAND_HUB: {
    MANAGE_PROFILE: 'manage-profile',
    CREATE_CREATIVE: 'create-creative',
  },
  // reports
  DELETE_REPORT: 'delete-report',
  SHARE_REPORT: 'share-report',
  DUPLICATE_REPORT: 'duplicate-report',
  AUTOMATE_REPORT: 'automate-report',
  INVITE_USER: 'invite-user',
  DISCONNECTED_PROFILES: 'disconnected-profiles',
  VIEW_REPORT: 'view-report',
};

export const routeNames = {
  [routes.LOGIN]: 'Login | Kontentino',
  [routes.RESET_PASSWORD]: 'Reset password | Kontentino',
  [routes.DASHBOARD]: 'Dashboard',
  [routes.POSTS]: 'Posts',
  [routes.IN_APP_NOTIFICATIONS]: 'Notifications',
  [routes.REPORTS]: 'Reports',
  [routes.INSIGHTS]: 'Analytics',
  [routes.INSIGHTS_POSTS_PERFORMANCE]: 'Analytics | Posts Performance',
  [routes.INSIGHTS_PAGE_PERFORMANCE]: 'Analytics | Page Performance',
  [routes.INSIGHTS_COMPETITION_ANALYSIS]: 'Analytics | Competition Analysis',
  [routes.INSIGHTS_PERFORMANCE_HUB]: 'Analytics | Performance Hub',
  [routes.ANALYTICS]: 'Analytics',
  [routes.ANALYTICS_PERFORMANCE]: 'Analytics | Performance',
  [routes.ANALYTICS_COMPETITION_ANALYSIS]: 'Analytics | Competition Analysis',
  [routes.ANALYTICS_REPORTS]: 'Analytics | Reports',
  [routes.POSTS_APPROVALS]: 'Approvals',
  [routes.BIO]: 'Bio Pages',
};

export default routes;
